import React, { useEffect, useRef, useState } from 'react';
import anime from 'animejs/lib/anime.es.js';
import { projects } from '../data/mock';
import { ExternalLink, Github, Calendar, TrendingUp, Sparkles, Award } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Projects = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const projectsRef = useRef(null);
  const filtersRef = useRef(null);
  const [filter, setFilter] = useState('All');
  const { currentColors } = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Enhanced title animation
            anime({
              targets: titleRef.current,
              opacity: [0, 1],
              translateY: [80, 0],
              rotateX: [90, 0],
              scale: [0.8, 1.1, 1],
              duration: 1200,
              easing: 'easeOutElastic(1, .6)'
            });

            // Animate projects grid
            setTimeout(() => {
              const projectCards = projectsRef.current.querySelectorAll('div');
              anime({
                targets: projectCards,
                opacity: [0, 1],
                translateY: [60, 0],
                scale: [0.9, 1],
                rotateX: [45, 0],
                duration: 800,
                delay: anime.stagger(150, { start: 400 }),
                easing: 'easeOutQuart'
              });
            }, 100);

            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleFilterChange = (newFilter) => {
    // Animate filter change
    const activeButton = document.querySelector('.filter-active');
    if (activeButton) {
      anime({
        targets: activeButton,
        scale: [1, 1.1, 1],
        duration: 300,
        easing: 'easeOutQuart'
      });
    }
    
    setFilter(newFilter);
  };

  // No filter categories - remove filter buttons
  const filteredProjects = projects;

  return (
    <section 
      id="projects" 
      ref={sectionRef} 
      className="py-16 md:py-32 relative z-10 transition-colors duration-500"
      style={{ backgroundColor: currentColors.cardBg }}
    >
      <div className="container mx-auto px-4 md:px-6">
        <h2 
          ref={titleRef}
          className="text-4xl md:text-6xl font-bold uppercase tracking-tight text-center mb-16 opacity-0 relative font-display"
          style={{ 
            color: currentColors.text
          }}
        >
          Projects
          <div 
            className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 h-1 w-32 opacity-80"
            style={{ backgroundColor: currentColors.accent }}
          />
        </h2>

        {/* Enhanced Projects Grid */}
        <div ref={projectsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 max-w-7xl mx-auto">
          {filteredProjects.map((project) => (
            <div 
              key={project.id}
              className="project-card rounded-xl p-6 md:p-8 transition-all duration-500 group cursor-pointer relative overflow-hidden opacity-0"
              style={{ 
                backgroundColor: currentColors.bg,
                border: `2px solid ${currentColors.borderLight}`,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = currentColors.accent;
                e.currentTarget.style.boxShadow = `0 12px 48px ${currentColors.accent}30`;
                anime({
                  targets: e.currentTarget,
                  scale: [1, 1.02],
                  translateY: [0, -8],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = currentColors.borderLight;
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.1)';
                anime({
                  targets: e.currentTarget,
                  scale: [1.02, 1],
                  translateY: [-8, 0],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              }}
            >
              {/* Project Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 
                      className="text-xl font-bold group-hover:scale-105 transition-transform duration-300"
                      style={{ color: currentColors.text }}
                    >
                      {project.title}
                    </h3>
                    {project.status === 'Featured' && (
                      <div 
                        className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wide font-bold"
                        style={{ 
                          backgroundColor: `${currentColors.accent}20`,
                          color: currentColors.accent
                        }}
                      >
                        <Award size={12} />
                        Featured
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <div 
                      className="flex items-center gap-2 text-xs font-mono uppercase tracking-wide"
                      style={{ color: currentColors.textSecondary }}
                    >
                      <Calendar size={14} />
                      {project.year}
                    </div>
                    <div 
                      className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-mono uppercase tracking-wide font-semibold"
                      style={{ 
                        backgroundColor: `${currentColors.accent}15`,
                        color: currentColors.accent
                      }}
                    >
                      {project.status}
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Description */}
              <p 
                className="mb-6 leading-relaxed"
                style={{ color: currentColors.textSecondary }}
              >
                {project.description}
              </p>

              {/* Technologies */}
              <div className="mb-6">
                <div 
                  className="font-mono text-xs uppercase tracking-wide font-semibold mb-3"
                  style={{ color: currentColors.textSecondary }}
                >
                  Tech Stack
                </div>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="tech-badge px-3 py-1.5 rounded-md text-xs font-mono font-semibold transition-all duration-300 hover:scale-110 hover:shadow-lg cursor-pointer"
                      style={{
                        backgroundColor: `${currentColors.accent}15`,
                        color: currentColors.text,
                        border: `1px solid ${currentColors.accent}30`
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = `${currentColors.accent}25`;
                        e.target.style.borderColor = currentColors.accent;
                        anime({
                          targets: e.target,
                          scale: [1, 1.15],
                          rotateZ: [0, 5, -5, 0],
                          duration: 400,
                          easing: 'easeOutElastic(1, .8)'
                        });
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = `${currentColors.accent}15`;
                        e.target.style.borderColor = `${currentColors.accent}30`;
                      }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Project Metrics */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div 
                  className="metric-card p-4 rounded-lg transition-all duration-300 hover:scale-105 cursor-pointer"
                  style={{ backgroundColor: `${currentColors.accent}10` }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentColors.accent}20`;
                    anime({
                      targets: e.currentTarget.querySelector('.metric-icon'),
                      rotateY: [0, 360],
                      duration: 600,
                      easing: 'easeOutQuart'
                    });
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentColors.accent}10`;
                  }}
                >
                  <div 
                    className="metric-icon flex items-center gap-2 text-xs font-mono uppercase tracking-wide font-semibold mb-2"
                    style={{ color: currentColors.textSecondary }}
                  >
                    <TrendingUp size={14} />
                    Performance
                  </div>
                  <div 
                    className="text-sm font-semibold"
                    style={{ color: currentColors.text }}
                  >
                    {project.metrics.performance}
                  </div>
                </div>
                <div 
                  className="metric-card p-4 rounded-lg transition-all duration-300 hover:scale-105 cursor-pointer"
                  style={{ backgroundColor: `${currentColors.accent}10` }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentColors.accent}20`;
                    anime({
                      targets: e.currentTarget.querySelector('.metric-icon'),
                      rotateY: [0, 360],
                      duration: 600,
                      easing: 'easeOutQuart'
                    });
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentColors.accent}10`;
                  }}
                >
                  <div 
                    className="metric-icon flex items-center gap-2 text-xs font-mono uppercase tracking-wide font-semibold mb-2"
                    style={{ color: currentColors.textSecondary }}
                  >
                    <Sparkles size={14} />
                    Impact
                  </div>
                  <div 
                    className="text-sm font-semibold"
                    style={{ color: currentColors.text }}
                  >
                    {project.metrics.impact}
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <button
                className="project-cta-btn w-full py-3 font-mono text-sm uppercase tracking-wide font-bold rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl flex items-center justify-center gap-2 group relative overflow-hidden"
                style={{
                  backgroundColor: currentColors.accent,
                  color: currentColors.bg
                }}
                onClick={(e) => {
                  // Ripple effect on click
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  
                  const ripple = document.createElement('span');
                  ripple.style.cssText = `
                    position: absolute;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.6);
                    width: 10px;
                    height: 10px;
                    left: ${x}px;
                    top: ${y}px;
                    transform: translate(-50%, -50%);
                    pointer-events: none;
                  `;
                  e.currentTarget.appendChild(ripple);
                  
                  anime({
                    targets: ripple,
                    width: ['10px', '300px'],
                    height: ['10px', '300px'],
                    opacity: [0.8, 0],
                    duration: 600,
                    easing: 'easeOutQuart',
                    complete: () => ripple.remove()
                  });
                  
                  // Button pulse animation
                  anime({
                    targets: e.currentTarget,
                    scale: [1, 0.95, 1.05, 1],
                    duration: 400,
                    easing: 'easeOutElastic(1, .6)'
                  });
                }}
                onMouseEnter={(e) => {
                  anime({
                    targets: e.currentTarget,
                    boxShadow: [`0 4px 20px ${currentColors.accent}40`, `0 8px 32px ${currentColors.accent}60`],
                    duration: 300,
                    easing: 'easeOutQuart'
                  });
                }}
              >
                <span>{project.link}</span>
                <ExternalLink size={16} className="group-hover:rotate-45 transition-transform duration-300" />
              </button>

              {/* Hover accent line */}
              <div 
                className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
                style={{ backgroundColor: currentColors.accent }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;